<template>
  <div>
    <!-- Loading -->
    <el-button v-if="isLoading" disabled>
      <i class="el-icon-loading mr-1"></i>
      Loading&hellip;
    </el-button>

    <!-- Promo card -->
    <div v-else :class="['calling-promo', { 'calling-promo--connected': isConnected }]">
      <span class="calling-promo__badge">
        <template v-if="isConnected">&#10003; Connected &mdash; Aloware CTI for HubSpot</template>
        <template v-else>&#9733; NEW &mdash; Aloware CTI for HubSpot (Beta)</template>
      </span>

      <h3 class="calling-promo__heading">
        A Better Calling Experience, Right Inside HubSpot
      </h3>
      <p class="calling-promo__subheading">
        Make and receive calls, log activity, and manage your workflow &mdash; all without leaving your CRM.
      </p>

      <el-row :gutter="16" class="calling-promo__grid">
        <el-col
          v-for="(feature, index) in features"
          :key="index"
          :span="12"
        >
          <div class="calling-promo__feature">
            <div class="calling-promo__feature-icon-box">
              <i :class="feature.icon" class="calling-promo__feature-icon"></i>
            </div>
            <div class="calling-promo__feature-text">
              <b>{{ feature.title }}</b>
              <span>{{ feature.desc }}</span>
            </div>
          </div>
        </el-col>
      </el-row>

      <div class="calling-promo__highlight">
        <i class="fa fa-bolt calling-promo__highlight-icon"></i>
        <span>
          Better call-state sync and fewer disconnects &mdash; plus
          <b>recording</b>, <b>transfers</b>, <b>VM drops</b>,
          <b>call parking</b>, <b>scripts</b> &amp; <b>wrap-up dispositions</b>,
          all without leaving HubSpot.
        </span>
      </div>

      <div class="calling-promo__cta">
        <!-- Connected -->
        <template v-if="isConnected">
          <el-button
            type="success"
            class="calling-promo__cta-btn calling-promo__cta-btn--connected"
            @click="dialogVisible = true"
          >
            <i class="fa fa-check-circle mr-1"></i>
            Aloware Calling Connected
          </el-button>
          <div class="calling-promo__cta-note">
            <span><b class="calling-promo__cta-note--success">You're all set up!</b> Aloware Calling is active on your account.</span>
            <span>Click the button to view connection details or manage settings.</span>
          </div>
        </template>

        <!-- Errored -->
        <template v-else-if="isErrored">
          <el-button
            type="danger"
            class="calling-promo__cta-btn calling-promo__cta-btn--errored"
            @click="dialogVisible = true"
          >
            <i class="fa fa-exclamation-circle mr-1"></i>
            Aloware Calling &mdash; Connection Error
          </el-button>
          <div class="calling-promo__cta-note">
            <span>There was a problem with your connection. Click to re-authenticate or test your setup.</span>
          </div>
        </template>

        <!-- Not connected -->
        <template v-else>
          <el-button
            type="warning"
            class="calling-promo__cta-btn"
            style="background-color: #E8601C; border-color: #E8601C; color: #fff; transition: all 0.2s ease;"
            @click="authenticate"
            @mouseenter.native="$event.currentTarget.style.backgroundColor='#b8470e'; $event.currentTarget.style.borderColor='#b8470e'; $event.currentTarget.style.boxShadow='0 4px 14px rgba(232,96,28,0.5)'; $event.currentTarget.style.transform='translateY(-1px)'"
            @mouseleave.native="$event.currentTarget.style.backgroundColor='#E8601C'; $event.currentTarget.style.borderColor='#E8601C'; $event.currentTarget.style.boxShadow='none'; $event.currentTarget.style.transform='none'"
          >
            <i class="fa fa-phone mr-1"></i>
            Set up Aloware Calling &mdash; Try it Now
          </el-button>
          <div class="calling-promo__cta-note">
            <span><b>Free to activate.</b> Takes less than 2 minutes.</span>
            <span>No additional installs required.</span>
          </div>
        </template>
      </div>
    </div>

    <!-- Management modal -->
    <el-dialog
      title="Aloware Calling"
      :visible.sync="dialogVisible"
      width="520px"
      append-to-body
      custom-class="calling-dialog"
    >
      <el-row type="flex" class="mb-2" style="margin-top: -8px;">
        <h5 class="status-label">Integration Status</h5>
        <el-switch
          v-if="callingModel"
          v-model="isEnabled"
          active-color="#00a344"
          @change="handleToggle"
        />
      </el-row>

      <div v-if="isLoading" class="d-flex align-items-center mt-2">
        <i class="el-icon-loading mr-2"></i>
        <span>Loading...</span>
      </div>

      <template v-else-if="callingModel">
        <!-- Connection Status -->
        <div v-if="callingModel" class="d-flex align-items-center mt-2">
          <connection-status
            :connection-status="callingModel.connection_status"
          />
        </div>

        <!-- Error Alert -->
        <el-alert
          v-if="fetchError"
          :title="fetchError"
          type="error"
          class="mt-2"
          show-icon
          :closable="false"
        />

        <!-- Re-Authenticate + Test Connection -->
        <el-row type="flex" class="mt-2">
          <el-tooltip class="item" effect="dark" placement="top">
            <div slot="content">
              <strong>Authentication</strong><br />
              <small>
                This will redirect you to HubSpot for Aloware Calling
                authentication.
              </small>
            </div>
            <el-button size="mini" class="btn-text" @click="authenticate">
              Re-Authenticate
            </el-button>
          </el-tooltip>

          <connection-tester-btn
            :integration-name="HUBSPOT_CALLING_INTEGRATION"
            integration-name-with-casing="Aloware Calling"
            :connection-status="callingModel.connection_status"
            class="ml-2 btn-text"
            @on-success="handleConnectionTest"
          />
        </el-row>

        <!-- Connection Info -->
        <div v-if="callingModel.credentials" class="mt-4">
          <h5 class="status-label">Connection Information</h5>
          <el-row :gutter="20" class="mt-2">
            <el-col :span="24">
              <span><b>Email:</b> {{ callingModel.credentials.user }}</span>
            </el-col>
            <el-col :span="24" class="mt-2">
              <span>
                <b>Account Name:</b> {{ callingModel.credentials.hub_domain }}
              </span>
            </el-col>
            <el-col :span="24" class="mt-2">
              <span>
                <b>Account ID:</b> {{ callingModel.credentials.hub_id }}
              </span>
            </el-col>
            <el-col
              v-if="callingModel.credentials.scopes"
              :span="24"
              class="mt-2"
            >
              <span><b>Scopes:</b></span>
              <div class="mt-1">
                <code>{{ callingModel.credentials.scopes }}</code>
              </div>
            </el-col>
            <el-col :span="24" class="mt-2">
              <span>
                <b>UI Domain:</b>
                {{
                  callingModel.credentials.uiDomain ||
                  'Please re-authenticate to fetch from HubSpot (default: app.hubspot.com)'
                }}
              </span>
            </el-col>
          </el-row>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios'
import { HUBSPOT_CALLING_INTEGRATION } from '../../../constants/integrations'
import { ConnectionStatus as ConnectionStatusEnum } from '../../../utils/integration-helpers'
import ConnectionStatus from '../connection-status.vue'
import ConnectionTesterBtn from '../connection-tester-btn.vue'

export default {
  name: 'HubspotCallingAppConnectionPane',

  components: { ConnectionStatus, ConnectionTesterBtn },

  data: () => ({
    isLoading: false,
    isEnabled: true,
    callingModel: null,
    fetchError: null,
    dialogVisible: false,
    HUBSPOT_CALLING_INTEGRATION,
    features: [
      {
        icon: 'fa fa-phone',
        title: 'Inbound + Outbound in One Place',
        desc: 'No more tab-switching — everything in HubSpot.'
      },
      {
        icon: 'fa fa-stop-circle',
        title: 'No Dropped Calls on Navigation',
        desc: "Tab changes and page refreshes won't interrupt your call."
      },
      {
        icon: 'fa fa-sliders-h',
        title: 'Full Controls in the Calling Window',
        desc: 'Mute, hold, transfer, VM drop, notes & tags — all accessible.'
      },
      {
        icon: 'fa fa-clipboard-list',
        title: 'Automatic Real-Time Logging',
        desc: 'Recordings, dispositions & activity logs — zero manual work.'
      },
      {
        icon: 'fa fa-th-large',
        title: 'Preview Dialer Across the CRM',
        desc: 'Call from Contacts, Deals, Tickets, Companies & more.'
      },
      {
        icon: 'fa fa-desktop',
        title: 'Works Even When Minimized',
        desc: 'Audio and logging keep running in the background.'
      }
    ]
  }),

  computed: {
    isConnected() {
      return (
        this.callingModel !== null &&
        this.callingModel !== undefined &&
        this.callingModel.connection_status === ConnectionStatusEnum.CONNECTED
      )
    },
    isErrored() {
      return (
        this.callingModel !== null &&
        this.callingModel !== undefined &&
        this.callingModel.connection_status !== ConnectionStatusEnum.CONNECTED
      )
    }
  },

  mounted() {
    this.fetchSettings()
  },

  methods: {
    async fetchSettings() {
      this.isLoading = true
      this.fetchError = null

      try {
        const response = await axios.get(
          `/api/v1/integration/${HUBSPOT_CALLING_INTEGRATION}`
        )
        const integration = response.data.integration

        if (
          integration &&
          integration.credentials &&
          integration.credentials !== 'null'
        ) {
          integration.credentials = JSON.parse(integration.credentials)
        }

        this.callingModel = integration
        this.isEnabled = true
      } catch (error) {
        if (error.response && error.response.status === 404) {
          this.callingModel = null
        } else {
          this.fetchError =
            'Failed to load Aloware Calling settings.'
        }
      } finally {
        this.isLoading = false
      }
    },

    authenticate() {
      window.location.href = '/integrations/hubspot-calling/oauth'
    },

    handleConnectionTest(success) {
      if (success) {
        this.callingModel.connection_status = ConnectionStatusEnum.CONNECTED
        this.$notify({
          title: 'Integration Connection',
          message: 'Your Aloware Calling connection is working!',
          type: 'success'
        })
        return
      }

      this.callingModel.connection_status = ConnectionStatusEnum.FAILED
      this.$notify({
        title: 'Integration Connection',
        message:
          'Unable to connect to Aloware Calling. Please re-authenticate using the button above.',
        type: 'error'
      })
    },

    async handleToggle(val) {
      if (val) {
        return
      }

      try {
        await axios.post('/api/v1/integration', {
          name: HUBSPOT_CALLING_INTEGRATION,
          status: false
        })
        this.callingModel = null
        this.dialogVisible = false
        this.$notify({
          title: 'Aloware Calling',
          message: 'The calling app integration has been disabled.',
          type: 'success'
        })
      } catch (e) {
        this.isEnabled = true
        this.$notify({
          message: 'Failed to disable the calling app integration.',
          type: 'error'
        })
      }
    }
  }
}
</script>

<style scoped>
.status-label {
  margin: 0 12px 0 0;
  font-size: 20px;
  color: #303133;
}

.btn-text {
  font-weight: 600 !important;
}

::v-deep .calling-dialog {
  border-radius: 8px;
  overflow: hidden;
}

/* ── Promo card ───────────────────────────────── */
.calling-promo {
  background-color: #FFF5EE;
  border: 1.5px solid #E8601C;
  border-radius: 12px;
  padding: 28px 32px;
  margin-bottom: 16px;
}

.calling-promo__badge {
  display: inline-block;
  background-color: #E8601C;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  padding: 4px 14px;
  border-radius: 20px;
  letter-spacing: 0.3px;
  margin-bottom: 16px;
}

.calling-promo__heading {
  font-size: 22px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 6px;
}

.calling-promo__subheading {
  font-size: 14px;
  color: #555;
  margin: 0 0 24px;
  line-height: 1.5;
}

/* Feature grid — 2 columns, equal-height rows */
.calling-promo__grid {
  margin-bottom: 8px;
  display: flex;
  flex-wrap: wrap;
}

::v-deep .calling-promo__grid > .el-col {
  display: flex;
}

.calling-promo__feature {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background-color: #fff;
  border: 1px solid #f0d4c4;
  border-radius: 10px;
  padding: 14px 16px;
  margin-bottom: 12px;
  width: 100%;
}

.calling-promo__feature-icon-box {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  min-width: 36px;
  background-color: #E8601C;
  border-radius: 8px;
}

.calling-promo__feature-icon {
  font-size: 16px;
  color: #fff;
}

.calling-promo__feature-text {
  display: flex;
  flex-direction: column;
  font-size: 13px;
  color: #333;
  line-height: 1.4;
}

.calling-promo__feature-text b {
  font-size: 14px;
  margin-bottom: 2px;
  color: #1a1a1a;
}

.calling-promo__feature-text span {
  color: #777;
  font-size: 13px;
}

/* Highlight bar */
.calling-promo__highlight {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background-color: #fff;
  border: 1px solid #f0d4c4;
  border-radius: 10px;
  padding: 14px 18px;
  margin-bottom: 24px;
  font-size: 13px;
  color: #333;
  line-height: 1.5;
}

.calling-promo__highlight-icon {
  color: #E8601C;
  font-size: 16px;
  flex-shrink: 0;
  margin-top: 2px;
}

/* CTA section */
.calling-promo__cta {
  display: flex;
  align-items: center;
  gap: 20px;
}


.calling-promo__cta-note {
  display: flex;
  flex-direction: column;
  font-size: 13px;
  color: #555;
  line-height: 1.5;
}

.calling-promo__cta-note b {
  color: #E8601C;
}

.calling-promo__cta-note b.calling-promo__cta-note--success {
  color: #00a344;
}

/* CTA button — uses type="warning" to avoid parent's ::v-deep .el-button--default green hover */
.calling-promo__cta-btn {
  background-color: #E8601C !important;
  border-color: #E8601C !important;
  color: #fff !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  padding: 12px 28px !important;
  border-radius: 8px !important;
  height: auto !important;
}

.calling-promo__cta-btn--connected {
  background-color: #00a344 !important;
  border-color: #00a344 !important;
}

.calling-promo__cta-btn--errored {
  background-color: #f56c6c !important;
  border-color: #f56c6c !important;
}

/* ── Connected green theme ──────────────────── */
.calling-promo--connected {
  background-color: #f0faf4;
  border-color: #00a344;
}

.calling-promo--connected .calling-promo__badge {
  background-color: #00a344;
}

.calling-promo--connected .calling-promo__feature {
  border-color: #c8e6d0;
}

.calling-promo--connected .calling-promo__feature-icon-box {
  background-color: #00a344;
}

.calling-promo--connected .calling-promo__highlight {
  border-color: #c8e6d0;
}

.calling-promo--connected .calling-promo__highlight-icon {
  color: #00a344;
}
</style>
